<template>
  <div class="owal-traininig-tool-detection-banner__container">
    <owal-event-banner
      :key="detection.detection_id"
      :detection-id="detection.detection_id"
      :full-frame="detection.frame_uri"
      :poster-frame-title="detection.camera_description"
      :poster-frame-subtitle="posterSubtitle"
      :unix-epoch-milliseconds="unixOffsetFromTimestamp(detection.captured_at)"
      :class="`float delay${(index*50)}ms`"
      :shapes="shapes"
      :clahe="clahe"
      :poster-buttons="posterButtons"
      >
      <div slot="content" class="owal flex row">
        <div class="owal matches container">
          <owal-training-tool-candidate-profile-button
            v-for="(candidateMatch, index) of visiblePotentialMatches"
            :key="candidateMatch.frontend_face_uuid"
            :match="candidateMatch"
            :first="notInTopFive ? false : index === 0"
            @click="confirmClicked(detection.detection_id, candidateMatch.frontend_face_uuid, candidateMatch.person_name)"
            />
        </div>
        <div v-if="!alreadyConfirmed" class="owal banner button area">
          <input v-model="notMatchFilter" placeholder="Someone else?">
          <div
            class="owal add face button"
            @click="addAFaceClicked"
          >
            New person
          </div>
          <div class="owal not face button" @click="notAFaceClicked">
            Not a Face
          </div>
        </div>
        <div v-else class="owal banner button area">
          <div class="owal undo button" @click="undoClicked(detection.detection_id, detection.matches[0].frontend_face_uuid)">
            Undo
          </div>
        </div>
      </div>
    </owal-event-banner>
    <owal-add-face
      v-if="showAddFace"
      :initial-image="base64Image"
      @close="handleClose"
    />
  </div>
</template>

<script>
import moment from 'moment'
import OwalEventBanner from '@/libraries/owal-ui/banners/OwalEventBanner'
import OwalTrainingToolCandidateProfileButton from './OwalTrainingToolCandidateProfileButton'
import OwalAddFace from './AddFace'
import { mapGetters } from 'vuex'
import { OwalButtonConfiguration } from '@/libraries/owal-ui'

export default {
  name: 'OwalTrainingToolDetectionBanner',
  components: {
    OwalEventBanner,
    OwalTrainingToolCandidateProfileButton,
    OwalAddFace,
  },
  /**
   * @prop {Object} face The face detected that needs a confirmation.
   * @prop {Number} index The index of the detection.
   * @prop {Boolean} shapes Does the banner show shapes on the poster?
   * @prop {Boolean} clahe Does the banner use Cntrast Limited Adaptive histogram equalization?
   */
  props: {
    detection: {
      Object,
    },
    index: {
      type: Number,
      default: 0,
    },
    shapes: {
      type: Boolean,
      default: false,
    },
    clahe: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      notMatchFilter: '',
      showAddFace: false,
      numberOfCandidateMatches: 4,
      OwalButtonConfiguration,
      base64Image: null,
    }
  },
  async created() {
    try {
      const b64 = await this.loadImageAsBase64(this.cropped_frame_uri)
      this.base64Image = b64
    } catch (e) {
      console.error(e)
      this.base64Image = this.cropped_frame_uri
    }
  },
  methods: {
    /**
     * Returns the unix offset, in milliseconds, represented by a string timestamp.
     * @param timestamp A timestamp string.
     * @returns An offset, in milliseconds, from the Unix epoch date.
     */
    unixOffsetFromTimestamp (timestamp) {
      return moment(timestamp).valueOf()
    },
    /*
     * Mark a face as not a face.
     */
    notAFaceClicked () {
      const index = this.index
      const payload = {
        index,
        detectionId: this.detection.detection_id,
        result: 'not-face',
      }
      this.$emit('reviewed', payload)
    },
    /**
     *  Add a new face
     */
    addAFaceClicked () {
      this.showAddFace = true
    },
    /**
     * Emits an event to the parent component with information about the confirmed face.
     */
    confirmClicked (detectionId, frontendFaceUUID, personName) {
      if (this.alreadyConfirmed) {
        return
      }
      const index = this.index
      const payload = {
        detectionId,
        frontendFaceUUID,
        index,
        result: personName === 'Not a Face' ? 'not-face' : 'confirmed',
      }
      this.notMatchFilter = ''
      this.$emit('reviewed', payload)
    },
    displayVideo () {
      this.$router.push({
        path: `/view/${this.detection.camera_uuid}/${this.detection.captured_at}`,
      })
    },
    undoClicked (detectionId, frontendFaceUUID) {
      const index = this.index
      const payload = {
        detectionId,
        frontendFaceUUID,
        index,
        result: 'undo',
      }
      this.notMatchFilter = ''
      this.$emit('reviewed', payload)
    },
    handleClose: function (payload) {
      this.showAddFace = false
    },
    async loadImageAsBase64 (url) {
      const response = await fetch(url)
      const img = await response.blob()
      const dataURL = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.readAsDataURL(img)
      })
      return dataURL
    },
  },
  computed: {
    ...mapGetters({
      frontendFaces: 'frontendFaces',
    }),
    /**
     * Search results, when none of the top five are correct.
     */
    filteredMatches () {
      const filtertext = this.notMatchFilter
      let matches = []
      if (!filtertext) {
        matches = this.frontendFaces
      } else {
        matches = this.frontendFaces.filter(face => {
          if (face.person_name.toLowerCase().includes(filtertext.toLowerCase())) {
            return true
          }
        })
      }
      const topFive = matches.slice(0, this.numberOfCandidateMatches).map(x => {
        // need to get an image_uri - first image that's a front facing image
        if (!x.images || !x.images.length) {
          return x
        }
        x.image_uri = (x.images.find(y => y.orientation === 'front') || {}).thumbnail_uri
        return x
      })
      return topFive
    },
    /**
     * @return `true` if the user has begun searching for another person.
     */
    notInTopFive () {
      return !!this.notMatchFilter
    },
    /**
     * The candidate matches to display.
     *
     * Either the initial top `N` candidates, or the filtered search results.
     * This will vary, based on if the not a match button is clicked.
     *
     */
    visiblePotentialMatches () {
      if (this.notInTopFive) {
        return this.filteredMatches
      } else if (this.detection.matches) {
        let max = Math.min(this.numberOfCandidateMatches, this.detection.matches.length)
        return this.detection.matches.slice(0, max)
      } else {
        return []
      }
    },
    /**
     * @return `true` if there is an exact match already.
     */
    alreadyConfirmed () {
      return !!this.detection.matches.find(x => x.is_exact)
    },
    posterSubtitle () {
      const detection = this.detection

      let conf = `${(parseFloat(detection.face_confidence) * 100).toFixed(2)}`

      if (parseInt(detection.face_confidence) === 1) {
        conf = '100'
      }

      const info = [
        `Face confidence: ${conf}%`,
        `Height: ${detection.face_height}px`,
      ]

      return info.join(' / ')
    },
    posterButtons () {
      return [
        new OwalButtonConfiguration({
          title: 'Full Frame',
          identifier: 'image',
          url: this.detection.frame_uri,
          alignment: 'center',
          accessoryType: 'none',
        }),
        new OwalButtonConfiguration({
          title: 'Video',
          identifier: 'video',
          url: `${window.location.protocol}//${window.location.host}/view/${this.detection.camera_uuid}/${this.detection.captured_at}`,
          alignment: 'center',
          accessoryType: 'none',
        }),
        new OwalButtonConfiguration({
          title: 'Not a Face',
          identifier: 'not-face',
          callback: this.notAFaceClicked,
          alignment: 'center',
          accessoryType: 'none',
          destructive: true,
        }),
      ]
    },
    cropped_frame_uri () {
      const url = new URL(this.detection.frame_uri)
      url.searchParams.set('cropped', '1')
      return url.toString()
    },
  },
}
</script>

<style scoped>

.owal.flex.row {
  display: flex;
  flex-direction: row;
  height: 100%;
  flex-shrink: 0;
  flex-grow: 1;
}

.owal.matches.container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  flex-grow: 1;
  height: 100%;
}

.owal-traininig-tool-detection-banner__container {
  display: flex;
  flex: initial;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 68.5714285714rem;
}

.owal.matches.container .owal.potential.match.container {
  flex-shrink: 0;
  height: 100%;
}

/* --- Based on https://codepen.io/shshaw/pen/wWQZEB --- */

/* Prevents :hover from triggering in the gaps between items */
.owal.matches.container {
   visibility: hidden;
}

/* Brings the child items back in, even though the parent is `hidden` */
.owal.matches.container > * {
  visibility: visible;
}

/* Makes the fades smooth with a slight delay to prevent jumps as the mouse moves between items. */
.owal.matches.container > * {
  transition: opacity 150ms linear 100ms, transform 150ms ease 100ms;
}

/* Fade out all items when the parent is hovered */
.owal.matches.container:hover > * {
  opacity: 0.4;
  transform: scale(0.9);
}

/* Fade in the currently hovered item */
.owal.matches.container > *:hover {
  opacity: 1;
  transform: scale(1);
  transition-delay: 0ms, 0ms;
}

/* --- end fade siblings code --- */

.owal.not.face.button,
.owal.undo.button {
  color: #fff;
  background: rgba(208,2,27, 0.9);
}

.owal.banner.button.area {
  display: flex;
  flex-direction: column;
  align-self: flex-end;
  padding: 0.5rem;
  margin: 0;
  flex-grow: 0;
  flex-shrink: 0;
  max-width: 10.5rem;
  height: 100%;
  align-content: stretch;
  align-items: stretch;
  justify-content: stretch;
}

.owal.banner.button.area .button {
  width: 6rem;
}

/* The buttons that control the non-match interfaces. */
.owal.not.match.button {
  font-family: 'Lato-Italic', 'Avenir-Italic', sans-serif;
  font-size: 0.8571428571rem;
  color: #475669;
  letter-spacing: 0;
  padding: 0.5rem;
  cursor: pointer;
  align-self: flex-end;
}

.owal.banner.button.area input {
  -webkit-appearance: none;
  flex-grow: 0;
  flex-shrink: 0;
  height: 2rem;
  width: 6.2rem;
  padding: 0 0.5rem;
  border: none;
  background: none;
  border-radius: 0.2857142857rem;
  font-family: 'Lato-Italic', 'Avenir-Italic', sans-serif;
  font-size: 0.8571428571rem;
  color: #475669;
  text-align: right;

  letter-spacing: 0;
  align-self: flex-end;
  text-align: right;
  cursor: pointer;

  transition: width 300ms ease, background-color 300ms ease;
}

.owal.banner.button.area input:hover {
  background: #E5E9F2;
}

input::-moz-placeholder,
input::-webkit-input-placeholder,
input::placeholder,
.owal.banner.button.area input:-ms-input-placeholder {
  color: #475669;
  opacity: 1;
}

.owal.banner.button.area input:focus {
  font-family: 'Lato-Regular', 'Avenir-Medium', sans-serif;
  text-align: left;
  background: #E5E9F2;
  border: 1px solid #D3DCE6;
  width: 8rem;
}

/* Add face button */
.owal.add.face.button,
.owal.video.button {
  background: #F0F0F0;
  color: #475669;
  border: 2px solid #E0E0E0;
}

.owal.banner.button.area * {
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  font-family: 'Lato-Regular', 'Avenir', sans-serif;
  font-size: 0.8571428571rem;
  height: 2rem;
  letter-spacing: 0;
  border-radius: 0.25rem;
  cursor: pointer;
  align-self: flex-end;

  padding: 0.5rem;
  margin: 0.5rem 0;
}

.owal.banner.button.area .button:hover {
  transform: scale(1.1);
  opacity: 0.8;
}

.owal.banner.button.area .button {
  transition: opacity 300ms ease, transform 300ms ease;
}

</style>
